'use client'

// libraries
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

// types
declare global {
	interface Window {
		dataLayer?: Array<Record<string, any>>
	}
}

// components
import Image from '/react4xp/components/Image'
import Link from '/react4xp/components/Link'
import RoundedBox from '/react4xp/components/RoundedBox'

// svg
import { Bachelor, FurtherEducation, Master, OneYear, Profession, SingleCourse } from '/react4xp/components/Icons/CourseLevels'
import { Clock, MapPin } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
export interface HeroSingleCourseProps {
    className?: string
    image?: {
        src?: string
        srcSmall?: string
        alt?: string
    }
    title?: string
    description?: string
    course?: {
        level?: 'bachelor' | 'further-education' | 'master' | 'one-year' | 'profession' | 'single-course'
        label?: string
    }
    location?: Array<{
        name: string
        url?: string
    }>
    duration?: string
    cost?: string
    requirements?: {
        label?: string
        url?: string
        external?: boolean
    }
    apply?: {
        cta?: {
            label: string
            url?: string
            external?: boolean
        }
        text?: string
    }
}

export default function HeroSingleCourse({
    className,
    image,
    title,
    description,
    course,
    location,
    duration,
    cost,
    requirements,
    apply
}: HeroSingleCourseProps) {
    const [isSticky, setIsSticky] = useState(false)
    const mainCtaRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!mainCtaRef.current) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // When main CTA goes out of view (not intersecting), show sticky
                    // When main CTA comes back into view (intersecting), hide sticky
                    setIsSticky(!entry.isIntersecting)
                })
            },
            {
                threshold: 0,
                rootMargin: '0px 0px 0px 0px'
            }
        )

        observer.observe(mainCtaRef.current)

        return () => {
            observer.disconnect()
        }
    }, [])

    return (
        <section className={clsx('hero-single-course', className)}>

            <div className='container'>
                <div className='row'>

                    <div className='col-12 col-lg-7'>
                        {image?.src && (
                            <div className='hero-single-course__image'>
                                <Image
                                    src={{
                                        src: image?.src || '',
                                        width: 800,
                                        height: 800
                                    }}
                                    srcSmall={{
                                        src: image?.srcSmall || '',
                                        width: 550,
                                        height: 400
                                    }}
                                    alt={image?.alt || ''}
                                    className='cover'
                                    lazyLoad={false}
                                    sizes='(max-width: 992px) 100vw, 800px'
                                />
                            </div>
                        )}
                    </div>
                    
                    <div className='col-12 col-lg-5 hero-single-course__content'>

                        <div className='hero-single-course__content__wrapper'>
                        
                            {title && (
                                <h1 className='h1'>
                                    {title}
                                </h1>
                            )}

                            {description && (
                                <p
                                    className='lead-paragraph'
                                    dangerouslySetInnerHTML={{ __html: description }}
                                    suppressHydrationWarning
                                />
                            )}

                            <div className='hero-single-course__content__course-details'>

                                {course && (
                                    <div className='item'>

                                        <div className='item__icon'>
                                            {course?.level === 'bachelor' && <Bachelor />}
                                            {course?.level === 'further-education' && <FurtherEducation />}
                                            {course?.level === 'master' && <Master />}
                                            {course?.level === 'one-year' && <OneYear />}
                                            {course?.level === 'profession' && <Profession />}
                                            {course?.level === 'single-course' && <SingleCourse />}
                                        </div>

                                        <p className='body-small'>
                                            {course?.label}
                                        </p>

                                    </div>
                                )}

                                {location && location.length > 0 && (
                                    <div className='item'>

                                        <div className='item__icon'>
                                            <MapPin />
                                        </div>

                                        <p className='body-small'>
                                            {location.map((loc, i) => (
                                                <span key={i}>
                                                    {loc.url
                                                        ? (
                                                            <Link
                                                                className='body-small'
                                                                href={loc.url}
                                                                isExternal={false}
                                                                label={{ underline: loc.name }}
                                                            />
                                                        )
                                                        : loc.name}
                                                    {i < location.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>

                                    </div>
                                )}

                                {duration && (
                                    <div className='item'>

                                        <div className='item__icon'>
                                            <Clock />
                                        </div>

                                        <p className='body-small'>
                                            {duration}
                                        </p>

                                    </div>
                                )}

                                {cost && (
                                    <p className='body-small'>
                                        {cost}
                                    </p>
                                )}

                                {requirements && (
                                    <Link
                                        className='body-small'
                                        href={requirements?.url}
                                        isExternal={requirements.external === true}
                                        label={{
                                            underline: requirements?.label
                                        }}
                                    />
                                )}

                            </div>

                        </div>

                        {(apply?.cta || apply?.text) && (
                            <div ref={mainCtaRef}>
                                <RoundedBox bgColor='green'>

                                    {apply?.cta && (
                                        <a
                                            href={apply?.cta?.url}
                                            target={apply?.cta?.external ? '_blank' : '_self'}
                                            rel={apply?.cta?.external ? 'noopener noreferrer' : ''}
                                            className='button-black'
                                            onClick={() => {
                                                if (window.dataLayer) {
                                                    window.dataLayer.push({
                                                        event: 'apply_course',
                                                        url: apply?.cta?.url
                                                    })
                                                }
                                            }}
                                        >
                                            {apply?.cta?.label}
                                        </a>
                                    )}

                                    {apply?.text && (
                                        <p className='body'>
                                            {apply?.text}
                                        </p>
                                    )}

                                </RoundedBox>
                            </div>
                        )}

                    </div>

                </div>
            </div>

            {apply?.cta && (
                <div className={clsx('hero-single-course__sticky-cta', { active: isSticky })}>
                    <RoundedBox bgColor='green'>

                        <a
                            href={apply?.cta?.url}
                            target={apply?.cta?.external ? '_blank' : '_self'}
                            rel={apply?.cta?.external ? 'noopener noreferrer' : ''}
                            className='button-black'
                        >
                            {apply?.cta?.label}
                        </a>

                        {apply?.text && (
                            <p className='body'>
                                {apply?.text}
                            </p>
                        )}

                    </RoundedBox>
                </div>
            )}
        </section>
    )
}